// src/pages/InsuranceManagement.js
import React, { useState, useContext, useEffect } from "react";
import ReactDOM from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import TabContext from "@/contexts/TabContext";
import { useListPageFilter, dateFormatToISOString } from "@/utils";
import { PageTabs, Pagination, Toast } from "@/components";
import { useGetMyRentCorpQuery, useGetInsuranceConfigListQuery } from "@/redux/rentCorpApi";
import { useGetTermListQuery } from "@/redux/termApi";
import { orderTypeOptions } from "@/constants/selectOptions";
import DatePicker from "react-datepicker";
import ko from "date-fns/locale/ko";
import Select from "react-select";

const StatusOptions = [
    { value: "", label: "전체" },
    { value: "ACTIVE", label: "사용" },
    { value: "INACTIVE", label: "미사용" },
];

// 페이지 기본 필터
const pageFilter = {
    parameters: {
        page: 0,
        size: 20,
        query: "",
        status: "",
        started_at: "",
        ended_at: "",
        rent_corp_id: null,
    },
};

export const InsuranceManagement = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { state: tabs, dispatch } = useContext(TabContext);

    const { formData, handleChange, setFormData, parameters, toastMsg } = useListPageFilter(pageFilter, {
        rentCorpId: null,
    });

    // 탭 추가 로직
    useEffect(() => {
        const handleTabAdd = () => {
            const existingTab = tabs.find((tab) => tab.url.split("?")[0] === location.pathname);
            if (!existingTab) {
                dispatch({
                    type: "ADD_TAB",
                    payload: { url: location.pathname + location.search, title: "보험 관리" },
                });
            }
        };
        handleTabAdd();
    }, [dispatch, location.pathname, location.search, tabs]);

    const { data, error: errorInsuranceConfig } = useGetInsuranceConfigListQuery(formData.rentCorpId, {
        skip: !formData.rentCorpId,
        refetchOnMountOrArgChange: true,
    });

    // 검색 버튼 클릭
    const handleSearch = () => {
        setFormData((prev) => ({
            ...prev,
            parameters: { ...prev.parameters, page: 0 },
            triggerSearch: true,
        }));
    };

    return (
        <>
            {toastMsg && <Toast visible={true} msg={toastMsg} setFormData={setFormData} />}

            <div className="management container">
                <PageTabs />

                <div className="table-container">
                    <div className="table-head">
                        <span className="head-tit">보험 관리</span>
                        <div className="breadcrumbs">
                            <ul>
                                <li className="home"></li>
                                <li>일반 관리</li>
                                <li>보험 관리</li>
                            </ul>
                        </div>
                    </div>

                    {/* 검색/필터 영역 */}
                    <TableFilter formData={formData} setFormData={setFormData} handleSearch={handleSearch} />

                    <div className="table-content">
                        <div className="table-option">
                            <div className="table-caption">
                                <span className="data-count">
                                    {data && data.pagination ? `전체 ${data.pagination.totalElements}건` : ""}
                                </span>
                            </div>

                            <div className="table-buttons">
                                <button
                                    type="button"
                                    className="btn-table_option btn-complete"
                                    onClick={() => navigate(`/admin/insurance-management/register`)}
                                >
                                    보험 등록
                                </button>
                            </div>
                        </div>

                        <div className="table-wrapper">
                            <div className="table">
                                <table>
                                    <colgroup>
                                        <col width="12%" />
                                        <col width="52%" />
                                        <col width="12%" />
                                        <col width="8%" />
                                        <col width="8%" />
                                    </colgroup>
                                    <thead>
                                        <tr>
                                            <th>
                                                <div className="cell">
                                                    <span className="cell-tit">사업자명</span>
                                                </div>
                                            </th>
                                            <th>
                                                <div className="cell">
                                                    <span className="cell-tit">보험 명칭</span>
                                                </div>
                                            </th>
                                            <th>
                                                <div className="cell">
                                                    <span className="cell-tit">적용 계약유형</span>
                                                </div>
                                            </th>
                                            <th>
                                                <div className="cell">
                                                    <span className="cell-tit">기본 보험 적용</span>
                                                </div>
                                            </th>
                                            <th>
                                                <div className="cell">
                                                    <span className="cell-tit">활성/비활성</span>
                                                </div>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data && data.data && data.data.length > 0 ? (
                                            data.data.map((item, index) => (
                                                <tr key={index}>
                                                    <td>
                                                        <div className="cell" data-align="center">
                                                            <span className="cell-desc">{item.rentCorp?.corpName}</span>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="cell" data-align="center">
                                                            <span
                                                                className="cell-link"
                                                                onClick={() => {
                                                                    navigate(`/admin/insurance-management/edit/${item.id}`, {
                                                                        state: { rentCorpId: formData.rentCorpId },
                                                                    });
                                                                }}
                                                            >
                                                                {item.name}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="cell" data-align="center">
                                                            <span className="cell-desc">
                                                                {item.contractTypes
                                                                    ?.map((type) => {
                                                                        const option = orderTypeOptions.find((opt) => opt.value === type);
                                                                        return option ? option.label : type;
                                                                    })
                                                                    .join(", ")}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="cell" data-align="center">
                                                            <span className="cell-desc">{item.defaultYn === "Y" ? "필수" : "선택"}</span>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="cell" data-align="center">
                                                            <span className="cell-desc">{item.useYn === "Y" ? "활성" : "비활성"}</span>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="5" className="data-empty">
                                                    <div className="cell" data-align="center">
                                                        <span className="cell-desc">데이터가 없습니다.</span>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {data && data.pagination && data.data && data.data.length > 0 && (
                            <Pagination pagination={data.pagination} setFormData={setFormData} />
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

/** 필터 컴포넌트 (VehicleManage.js와 유사) */
const TableFilter = ({ formData, setFormData, handleSearch }) => {
    const { parameters } = formData;
    const [activeButton, setActiveButton] = useState(null);

    const handleParameterChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            parameters: {
                ...prev.parameters,
                [name]: value,
            },
        }));
    };

    const handleDateChange = (dates) => {
        const [start, end] = dates;
        setFormData((prev) => ({
            ...prev,
            parameters: {
                ...prev.parameters,
                page: 0,
                started_at: start ? dateFormatToISOString(start, "start") : "",
                ended_at: end ? dateFormatToISOString(end, "end") : "",
            },
        }));
        setActiveButton(null);
    };

    const setDateRange = (days, buttonIndex) => {
        const now = new Date();
        const start = new Date(now);
        start.setDate(start.getDate() - days);
        setFormData((prev) => ({
            ...prev,
            parameters: {
                ...prev.parameters,
                page: 0,
                started_at: dateFormatToISOString(start, "start"),
                ended_at: dateFormatToISOString(now, "end"),
            },
        }));
        setActiveButton(buttonIndex);
    };

    // 사업자 리스트 조회
    const { data: myRentCorpData } = useGetMyRentCorpQuery();
    const corpOptions =
        myRentCorpData && myRentCorpData.data
            ? myRentCorpData.data.map((item) => ({
                  value: item.id,
                  label: item.corpName,
              }))
            : [];

    return (
        <div className="table-filter">
            <div className="filter-row">
                <div className="filter-item">
                    <div className="item-head">
                        <span className="head-tit">사업자 :</span>
                    </div>
                    <div className="item-content">
                        <div className="item-select">
                            <Select
                                options={corpOptions}
                                className="filter-select"
                                classNamePrefix="filter-select"
                                isSearchable={false}
                                placeholder="사업자 선택"
                                value={corpOptions.find((opt) => opt.value === formData.rentCorpId)}
                                onChange={(option) =>
                                    setFormData((prev) => ({
                                        ...prev,
                                        rentCorpId: option.value,
                                    }))
                                }
                            />
                        </div>
                    </div>
                </div>
                <div className="filter-item">
                    <div className="item-content">
                        <div className="item-input" style={{ width: "300px" }}>
                            <input
                                type="text"
                                placeholder="입력하세요."
                                name="query"
                                value={parameters.query}
                                onChange={handleParameterChange}
                            />
                        </div>
                        <div className="item-buttons">
                            <button type="button" className="btn-search" onClick={handleSearch}>
                                검색
                            </button>
                            <button
                                type="button"
                                className="btn-search"
                                onClick={() => {
                                    // 필터 초기화
                                    setFormData((prev) => ({
                                        ...prev,
                                        parameters: { ...pageFilter.parameters },
                                    }));
                                    setActiveButton(null);
                                }}
                            >
                                초기화
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
